import {gql} from '@apollo/client';
import {css, StyleSheet} from 'aphrodite';
import {DocumentNode} from 'graphql';
import React, {useRef, useEffect} from 'react';
import {connect} from 'react-redux';
import {bindActionCreators} from 'redux';

import WorkspaceItemInaccessibleGuard from 'auth/WorkspaceItemInaccessibleGuard';
import BenchlingConfig from 'BenchlingConfig';
import {pluralize} from 'BenchlingUtil';
import {QueryResultType, useBenchlingQuery, BenchlingQueryResult} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {graphQLArrayToREST, graphQLToREST} from 'graphql-client/declarative-translations/external/translate';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {LoadingFileScreen} from 'nav/full-screen/LoadingFullScreen';
import {BenchlingDispatch, Thunk} from 'redux-stores/Store';
import {AssaySchema} from 'results/assay-schemas/AssaySchemaConstants';
import {AssayResult, AssayRun} from 'results/assays/AssayRecordConstants';
import {fetchFilteredAssayResults} from 'results/assays/FilteredAssayResultsActions';
import StandaloneResultsView from 'results/assays/StandaloneResultsView';
import legalResultSchemasForRun from 'results/legalResultSchemasForRun';
import {MODEL_NAMES} from 'results/ResultsNamingConstants';
import SetValidationButton from 'results/validation/SetValidationButton';
import {validatableInputsForRun} from 'results/validation/ValidatableInput';
import {UploadResultsButton} from 'runs/assay-group/AssayGroupEditButtons';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';

import {RunResultsQuery, RunResultsQueryVariables} from './__generated/RunResultsQuery';

interface RunResultsProps {
  run: AssayRun;
  resultSchemas: Array<AssaySchema>;
  linkedResults: Array<AssayResult>;
}

interface DispatchProps {
  fetchFilteredAssayResults: () => Thunk<Promise<void>>;
}

export function RunResults(props: RunResultsProps & DispatchProps): JSX.Element {
  const validationWarningMessage = props.linkedResults.length
    ? `Validation will be set on ${props.linkedResults.length} ${pluralize(
        props.linkedResults.length,
        MODEL_NAMES.result
      )} linked to this ${MODEL_NAMES.run}.`
    : null;

  return (
    <div className="react-tab-container react-tab-container--mediocre-contentWrapper">
      <Row bufferRowReset={true}>
        <Col xs={6} className={css(styles.title)}>
          {MODEL_NAMES.Results}
        </Col>
        <Col xs={6}>
          <div className="u-justify-content-end">
            {props.run.permissions.writable && (
              <SetValidationButton
                modalWarningMessage={validationWarningMessage}
                size="sm"
                validatableInputs={validatableInputsForRun(props.run)}
              />
            )}
            <UploadResultsButton
              assayRun={props.run}
              resultSchemasForRun={props.resultSchemas}
              fetchAssayResultsByRun={props.fetchFilteredAssayResults}
            />
          </div>
        </Col>
      </Row>
      <div className="u-small-spacing-top">
        <StandaloneResultsView
          emptyMessage={`No ${MODEL_NAMES.results} are linked to this run`}
          filters={{assayRunId: props.run.id}}
          includeResultsAgainstArchivedSchemas={BenchlingConfig.RESULTS_SHOW_ARCHIVED_SCHEMAS_ON_RESULTS_TAB}
        />
      </div>
    </div>
  );
}

const mapDispatchToProps = (dispatch: BenchlingDispatch, props: RunResultsProps): DispatchProps =>
  bindActionCreators(
    {
      fetchFilteredAssayResults: () => fetchFilteredAssayResults({assayRunId: props.run.id}),
    },
    dispatch
  );
const ConnectedRunResults = connect(null, mapDispatchToProps)(RunResults);

export const RUN_RESULTS_QUERY: DocumentNode = gql`
  query RunResultsQuery($runId: StrictString!, $offset: StrictInt!)
  @skipAudit
  @engComponent(name: LAB_AUTOMATION) {
    run(id: $runId) {
      ... on RunNode {
        ...RunGraphQL
        linkedResults(limitOffsetInput: {limit: 100, offset: $offset}) {
          ... on ResultListingNode {
            total
            results {
              ...ResultGraphQL
            }
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
    ...AllResultSchemasGraphQL
  }
  ${getFragmentWrapper('Run').fragment}
  ${getFragmentWrapper('Result').fragment}
  ${getFragmentWrapper('AllResultSchemas').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export default function QueryingRunResults(props: {runId: string}): JSX.Element {
  const result = useBenchlingQuery<RunResultsQuery, RunResultsQueryVariables>(RUN_RESULTS_QUERY, {
    variables: {runId: props.runId, offset: 0},
    fetchPolicy: 'cache-and-network',
  });
  const ref = useRef<BenchlingQueryResult<RunResultsQuery, RunResultsQueryVariables>>();
  useEffect(() => {
    if (result.type === QueryResultType.SUCCESS && result.data && result.fetchMore) {
      ref.current = result;
      if (
        !isInaccessible(result.data.run) &&
        result.data.run.linkedResults.results.length < result.data.run.linkedResults.total
      ) {
        result.fetchMore({variables: {offset: result.data.run.linkedResults.results.length}});
      }
    }
  }, [result]);
  const prevResult = ref.current;
  let data;
  if (result.type === QueryResultType.PENDING && prevResult && prevResult.type === QueryResultType.SUCCESS) {
    data = prevResult.data;
  } else if (result.type === QueryResultType.SUCCESS) {
    data = result.data;
  } else {
    return <LoadingFileScreen />;
  }
  if (isInaccessible(data.run)) {
    return <WorkspaceItemInaccessibleGuard inaccessibleInfo={data.run} modelType="run" />;
  }

  return (
    <ConnectedRunResults
      {...props}
      run={graphQLToREST('Run', data.run)}
      resultSchemas={legalResultSchemasForRun(
        graphQLArrayToREST('ResultSchema', data.resultSchemas),
        data.run.schema.id
      )}
      linkedResults={graphQLArrayToREST('Result', data.run.linkedResults.results)}
    />
  );
}

export const styles = StyleSheet.create({
  title: {
    fontSize: '17px',
  },
});
